import { Dispatch } from 'react';
import { ChatMessage, SendMessageData } from '../schemas/chat';
import { ChatAction } from '../contexts/chatReducer';
import chatService from '../services/chatService';

interface UseMessageActionsParams {
  dispatch: Dispatch<ChatAction>;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  updateConversation?: (id: string, data: Partial<{ title: string }>) => Promise<void>;
  updateConversationTitle?: (id: string, title: string, persistToBackend?: boolean) => void;
  isNewConversation?: boolean;
}

export const useMessageActions = ({
  dispatch,
  setLoading,
  setError,
  clearError,
  updateConversation,
  updateConversationTitle,
  isNewConversation,
}: UseMessageActionsParams) => {
  const sendMessage = async (conversationId: string, data: SendMessageData) => {
    try {
      // ========================================
      // 1. 初始化：设置加载状态
      // ========================================
      setLoading(true);

      // ========================================
      // 2. 立即添加用户消息（乐观更新）
      // ========================================
      const userMessage: ChatMessage = {
        id: `temp-${Date.now()}`,
        role: 'user',
        content: data.content,
        created_at: new Date().toISOString(),
        files: data.files,
        is_done: true,
      };
      dispatch({ type: 'ADD_MESSAGE', payload: { conversationId, message: userMessage } });

      // ========================================
      // 3. 发送请求并获取响应
      // ========================================
      const response = await chatService.sendMessage(conversationId, data);

      // ========================================
      // 4. 添加助手回复消息
      // ========================================
      dispatch({ type: 'ADD_MESSAGE', payload: { conversationId, message: response.message } });

      // ========================================
      // 5. 如果是新对话，更新对话标题
      // ========================================
      if (isNewConversation && updateConversation) {
        const title =
          data.content.trim().length > 50
            ? data.content.trim().substring(0, 50) + '...'
            : data.content.trim();
        await updateConversation(conversationId, { title });
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  const sendMessageStream = async (
    conversationId: string,
    data: SendMessageData,
    onChunk?: (chunk: string) => void,
    onThinkingChunk?: (chunk: string) => void,
    onTitleChunk?: (title: string) => void
  ) => {
    try {
      // ========================================
      // 1. 初始化：设置加载状态，清空错误
      // ========================================
      setLoading(true);
      clearError();

      // ========================================
      // 2. 立即添加用户消息（乐观更新）
      // ========================================
      const userMessage: ChatMessage = {
        id: `user-${Date.now()}`,
        role: 'user',
        content: data.content,
        created_at: new Date().toISOString(),
        files: data.files,
        is_done: true,
      };
      dispatch({ type: 'ADD_MESSAGE', payload: { conversationId, message: userMessage } });

      // ========================================
      // 3. 创建助手消息占位符
      // ========================================
      const assistantMessageId = `assistant-${Date.now()}`;
      const assistantMessage: ChatMessage = {
        id: assistantMessageId,
        role: 'assistant',
        content: '',
        created_at: new Date().toISOString(),
        is_done: false,
      };
      dispatch({ type: 'ADD_MESSAGE', payload: { conversationId, message: assistantMessage } });

      // Mark streaming as active in ChatContext
      dispatch({
        type: 'SET_STREAMING',
        payload: { conversationId, messageId: assistantMessageId, isStreaming: true },
      });

      // ========================================
      // 4. 准备流式处理
      // ========================================
      let accumulatedContent = '';
      let accumulatedThinking = '';

      const handleChunk = (chunk: string) => {
        if (onChunk) onChunk(chunk);
        accumulatedContent += chunk;
        dispatch({
          type: 'UPDATE_MESSAGE',
          payload: {
            conversationId,
            messageId: assistantMessageId,
            message: {
              content: accumulatedContent,
              is_done: false,
            },
          },
        });
      };

      const handleThinkingChunk = (chunk: string) => {
        if (onThinkingChunk) onThinkingChunk(chunk);
        accumulatedThinking += chunk;
        dispatch({
          type: 'UPDATE_MESSAGE',
          payload: {
            conversationId,
            messageId: assistantMessageId,
            message: {
              thinking_process: accumulatedThinking
                ? {
                    steps: accumulatedThinking.split('\n').length,
                    content: accumulatedThinking,
                  }
                : undefined,
              is_done: false,
            },
          },
        });
      };

      const handleComplete = async (message: ChatMessage) => {
        // 统一使用 UPDATE_MESSAGE 来更新消息，避免消息闪烁
        // 重要：不要更新 message.id，因为 React 使用它作为 key
        // 改变 ID 会导致组件重新挂载，造成消息消失
        const thinkingContent = message.thinking_process?.content || '';
        dispatch({
          type: 'UPDATE_MESSAGE',
          payload: {
            conversationId,
            messageId: assistantMessageId,
            message: {
              // 保留原有的 assistantMessageId 作为 React key
              id: assistantMessageId,
              content: message.content,
              // API field
              thinking_process: thinkingContent
                ? {
                    steps: thinkingContent.split('\n').length,
                    content: thinkingContent,
                  }
                : undefined,
              is_done: true,
              model_name: message.model_name,
              model_id: message.model_id,
              usage: message.usage,
            },
          },
        });

        // Mark streaming as complete in ChatContext
        dispatch({
          type: 'SET_STREAMING',
          payload: { conversationId, messageId: assistantMessageId, isStreaming: false },
        });

        // Note: Removed loadConversations() call to prevent overwriting optimistic message updates
        // with stale data from the backend. The conversation will be refreshed naturally when
        // the user navigates or performs other actions.
      };

      // Track the last processed title to avoid duplicate updates
      let lastProcessedTitle: string | null = null;

      const handleTitleChunk = (title: string) => {
        // Only process if title has changed
        if (title !== lastProcessedTitle) {
          lastProcessedTitle = title;
          console.log(`[useMessageActions] Received title update: "${title}"`);
          // Forward to the original callback if provided
          if (onTitleChunk) {
            onTitleChunk(title);
          }
          // Update conversation title immediately with backend persistence
          if (updateConversationTitle) {
            updateConversationTitle(conversationId, title, true); // true = persist to backend
          }
        }
      };

      const handleError = (error: Error) => {
        dispatch({
          type: 'UPDATE_MESSAGE',
          payload: {
            conversationId,
            messageId: assistantMessageId,
            message: {
              error_data: error instanceof Error ? { message: error.message } : error,
              is_done: true,
            },
          },
        });

        // Mark streaming as complete in ChatContext (on error)
        dispatch({
          type: 'SET_STREAMING',
          payload: { conversationId, messageId: assistantMessageId, isStreaming: false },
        });

        setError(error.message);
      };

      // ========================================
      // 5. 执行流式请求
      // ========================================
      await chatService.sendMessageStream(
        conversationId,
        data,
        handleChunk,
        handleThinkingChunk,
        handleComplete,
        handleError,
        handleTitleChunk
      );

      // ========================================
      // 6. 如果是新对话，更新对话标题
      // ========================================
      if (isNewConversation && updateConversation) {
        const title =
          data.content.trim().length > 50
            ? data.content.trim().substring(0, 50) + '...'
            : data.content.trim();
        await updateConversation(conversationId, { title });
      }
    } catch (error) {
      // ========================================
      // 7. 错误处理
      // ========================================
      setError(error instanceof Error ? error.message : 'Failed to send message');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Resume streaming a message that was interrupted
   * This is called automatically when a message with is_done=false is detected
   * @param conversationId The ID of the conversation
   * @param messageId The ID of the message to resume
   */
  const resumeMessageStream = async (conversationId: string, messageId: string) => {
    try {
      console.log(`[useMessageActions] Resuming stream for message ${messageId}`);
      setLoading(true);
      clearError();

      // Mark streaming as active in ChatContext
      dispatch({
        type: 'SET_STREAMING',
        payload: { conversationId, messageId, isStreaming: true },
      });

      // Prepare for streaming - get existing message to start from
      let accumulatedContent = '';
      let accumulatedThinking = '';

      const handleChunk = (chunk: string) => {
        accumulatedContent += chunk;
        dispatch({
          type: 'UPDATE_MESSAGE',
          payload: {
            conversationId,
            messageId,
            message: {
              content: accumulatedContent,
              is_done: false,
            },
          },
        });
      };

      const handleThinkingChunk = (chunk: string) => {
        accumulatedThinking += chunk;
        dispatch({
          type: 'UPDATE_MESSAGE',
          payload: {
            conversationId,
            messageId,
            message: {
              thinking_process: accumulatedThinking
                ? {
                    steps: accumulatedThinking.split('\n').length,
                    content: accumulatedThinking,
                  }
                : undefined,
              is_done: false,
            },
          },
        });
      };

      const handleComplete = (message: ChatMessage) => {
        const thinkingContent = message.thinking_process?.content || '';
        dispatch({
          type: 'UPDATE_MESSAGE',
          payload: {
            conversationId,
            messageId,
            message: {
              content: message.content,
              thinking_process: thinkingContent
                ? {
                    steps: thinkingContent.split('\n').length,
                    content: thinkingContent,
                  }
                : undefined,
              is_done: true,
              model_name: message.model_name,
              model_id: message.model_id,
              usage: message.usage,
            },
          },
        });

        // Mark streaming as complete in ChatContext
        dispatch({
          type: 'SET_STREAMING',
          payload: { conversationId, messageId, isStreaming: false },
        });
      };

      const handleError = (error: Error) => {
        dispatch({
          type: 'UPDATE_MESSAGE',
          payload: {
            conversationId,
            messageId,
            message: {
              error_data: error instanceof Error ? { message: error.message } : error,
              is_done: true,
            },
          },
        });

        // Mark streaming as complete in ChatContext (on error)
        dispatch({
          type: 'SET_STREAMING',
          payload: { conversationId, messageId, isStreaming: false },
        });

        setError(error.message);
      };

      // Execute the resume stream request
      await chatService.resumeMessageStream(
        conversationId,
        messageId,
        handleChunk,
        handleThinkingChunk,
        handleComplete,
        handleError
      );
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to resume message stream');
    } finally {
      setLoading(false);
    }
  };

  const updateMessage = async (conversationId: string, messageId: string, content: string) => {
    try {
      // ========================================
      // 1. 更新消息
      // ========================================
      const updatedMessage = await chatService.updateMessage(conversationId, messageId, content);

      // ========================================
      // 2. 更新状态
      // ========================================
      dispatch({
        type: 'UPDATE_MESSAGE',
        payload: { conversationId, messageId, message: updatedMessage },
      });
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to update message');
    }
  };

  const deleteMessage = async (conversationId: string, messageId: string) => {
    try {
      // ========================================
      // 1. 删除消息
      // ========================================
      await chatService.deleteMessage(conversationId, messageId);

      // ========================================
      // 2. 更新状态
      // ========================================
      dispatch({ type: 'DELETE_MESSAGE', payload: { conversationId, messageId } });
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to delete message');
    }
  };

  const regenerateMessage = async (conversationId: string, messageId: string) => {
    try {
      // ========================================
      // 1. 初始化：设置加载状态
      // ========================================
      setLoading(true);

      // ========================================
      // 2. 重新生成消息
      // ========================================
      const response = await chatService.regenerateMessage(conversationId, messageId);

      // ========================================
      // 3. 更新状态
      // ========================================
      dispatch({
        type: 'UPDATE_MESSAGE',
        payload: {
          conversationId,
          messageId,
          message: response.message,
        },
      });
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to regenerate message');
    } finally {
      setLoading(false);
    }
  };

  return {
    sendMessage,
    sendMessageStream,
    resumeMessageStream,
    updateMessage,
    deleteMessage,
    regenerateMessage,
  };
};
